import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { PaginatedDto } from '@services/extendedType'
import {
  AcquisitionProtocolDto,
  CreateAcquisitionPoroctolDto,
  FindAcquisitionProtocolsQuery,
  UpdateAcquisitionProtocolDto,
} from '../../services/dtos/AcquisitionProtocolDto'
import { AcquisitionProtocolsService } from '../../services/AcquisitionProtocol'

export interface AcquisitionProtocolState {
  acquisitionProtocols: PaginatedDto<AcquisitionProtocolDto>
}

const intialState: AcquisitionProtocolState = {
  acquisitionProtocols: {
    datas: [],
    totalCount: 0,
    page: 1,
  },
}

export const doCreateAcquisitionProtocol = createAsyncThunk<
  AcquisitionProtocolDto,
  CreateAcquisitionPoroctolDto
>(
  'acquisitionProtocols/createAcquisitionProtocol',
  async (query, { rejectWithValue }) => {
    try {
      return await AcquisitionProtocolsService.create(query)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const doFindAcquisitionProtocols = createAsyncThunk<
  PaginatedDto<AcquisitionProtocolDto>,
  FindAcquisitionProtocolsQuery | undefined
>(
  'acquisitionProtocols/findAcquisitionProtocols',
  async (query, { rejectWithValue }) => {
    try {
      return await AcquisitionProtocolsService.findAll(query)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const doUpdateAcquisitionProtocols = createAsyncThunk<
  AcquisitionProtocolDto,
  { id: number; dto: UpdateAcquisitionProtocolDto }
>(
  'acquisitionProtocols/updateAcquisitionProtocols',
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      return await AcquisitionProtocolsService.update(id, dto)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const doDeleteAcquisitionProtocols = createAsyncThunk<void, number>(
  'acquisitionProtocols/deleteAcquisitionProtocols',
  async (id, { rejectWithValue }) => {
    try {
      await AcquisitionProtocolsService.remove(id)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const doBatchImportMultiStepProtocols = createAsyncThunk(
  'acquisitionProtocols/batchImportMultiStepProtocols',
  async (
    { file, siteId }: { file: File; siteId: number },
    { rejectWithValue },
  ) => {
    try {
      await AcquisitionProtocolsService.batchMultiStepProtocolImport({
        file,
        siteId,
      })
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const acquisitionProtoSlice = createSlice({
  name: 'acqusitionProtocols',
  initialState: intialState,
  reducers: {
    setAcquisitionProtocols: (state, action) => {
      state.acquisitionProtocols = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(doCreateAcquisitionProtocol.fulfilled, (state, action) => {
        state.acquisitionProtocols.datas.unshift(action.payload)
      })
      .addCase(doFindAcquisitionProtocols.fulfilled, (state, action) => {
        state.acquisitionProtocols = action.payload
      })
      .addCase(doUpdateAcquisitionProtocols.fulfilled, (state, action) => {
        const index = state.acquisitionProtocols.datas.findIndex(
          (proto) => proto.id === action.payload.id,
        )
        state.acquisitionProtocols.datas[index] = action.payload
      })
      .addCase(doDeleteAcquisitionProtocols.fulfilled, (state, action) => {
        state.acquisitionProtocols.datas =
          state.acquisitionProtocols.datas.filter(
            (proto) => proto.id !== action.meta.arg,
          )
      })
  },
})

export default acquisitionProtoSlice.reducer
